<template>
  <section
    v-if="topSelfClosePercent !== null"
    class="bg-white py-16 tablet:py-24"
  >
    <div class="container grid items-center gap-10 tablet:grid-cols-2 tablet:gap-16 [&>*]:min-w-0">
      <!-- Copy -->
      <div class="flex flex-col gap-5">
        <i18n-t
          keypath="landing.payoutFeature.title"
          scope="global"
          tag="h2"
          class="text-3xl font-bold tracking-tight tablet:text-[44px] tablet:leading-[1.1]"
        >
          <template #br><br /></template>
          <template #accent
            ><span class="text-gold-primary-120">{{ t('landing.payoutFeature.titleAccent') }}</span></template
          >
        </i18n-t>
        <p class="text-base text-black-80 tablet:text-lg">
          {{ t('landing.payoutFeature.description', descriptionParams) }}
        </p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="tag in tagPills"
            :key="tag"
            class="inline-flex items-center rounded-full bg-gold-primary-10 px-3 py-1.5 text-xs font-medium text-gold-primary-120"
          >
            {{ t(`landing.payoutFeature.tags.${tag}`, { percent: example.insidersPercent }) }}
          </span>
        </div>
        <p class="mt-2 text-sm font-bold italic text-black-100">
          {{ t('landing.payoutFeature.closing') }}
        </p>
      </div>

      <!-- Payout card -->
      <div
        ref="cardRef"
        class="rounded-2xl bg-white shadow-xl shadow-black/5 transition-[transform,opacity] duration-500"
        :class="isRevealed ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'"
      >
        <!-- Browser chrome -->
        <div
          class="flex items-center gap-3 rounded-t-2xl border-b border-black/[0.06] bg-[#FAF7F0] px-4 py-3 tablet:px-5"
        >
          <div class="flex shrink-0 items-center gap-1.5">
            <span class="size-2.5 rounded-full bg-black/10"></span>
            <span class="size-2.5 rounded-full bg-black/10"></span>
            <span class="size-2.5 rounded-full bg-black/10"></span>
          </div>
          <div class="flex flex-1 items-center justify-center">
            <div
              class="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1 text-[11px] text-black/55 ring-1 ring-black/[0.06]"
            >
              <svg
                viewBox="0 0 24 24"
                class="size-2.5"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <rect
                  x="4"
                  y="11"
                  width="16"
                  height="10"
                  rx="2"
                />
                <path d="M8 11V7a4 4 0 018 0v4" />
              </svg>
              <span class="font-mono">insidersofdubai.com/dashboard/damac-lagoons</span>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-1.5">
            <span class="relative flex size-1.5">
              <span class="absolute inline-flex size-full animate-ping rounded-full bg-green-500 opacity-75"></span>
              <span class="relative inline-flex size-1.5 rounded-full bg-green-500"></span>
            </span>
            <span class="hidden text-[10px] font-medium uppercase tracking-wider text-black/50 tablet:inline">{{
              t('landing.payoutFeature.liveSession')
            }}</span>
          </div>
        </div>

        <div class="p-6 tablet:p-7">
          <!-- Project lockup with inline skyline SVG -->
          <div class="mb-6 flex items-center gap-4 tablet:gap-5">
            <div
              class="relative h-[90px] w-[120px] shrink-0 overflow-hidden rounded-xl ring-1 ring-black/10 tablet:h-[105px] tablet:w-[140px]"
            >
              <svg
                viewBox="0 0 140 90"
                preserveAspectRatio="xMidYMid slice"
                class="absolute inset-0 size-full"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient
                    id="payoutSky"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0"
                      stop-color="#FFE4B0"
                    />
                    <stop
                      offset="1"
                      stop-color="#FFB87A"
                    />
                  </linearGradient>
                </defs>
                <rect
                  width="140"
                  height="70"
                  fill="url(#payoutSky)"
                />
                <circle
                  cx="105"
                  cy="22"
                  r="10"
                  fill="#FFF3D6"
                />
                <rect
                  x="10"
                  y="30"
                  width="16"
                  height="40"
                  fill="#8A6834"
                />
                <rect
                  x="28"
                  y="18"
                  width="20"
                  height="52"
                  fill="#A17A3C"
                />
                <rect
                  x="50"
                  y="36"
                  width="12"
                  height="34"
                  fill="#6E5329"
                />
                <rect
                  x="64"
                  y="22"
                  width="22"
                  height="48"
                  fill="#9B7536"
                />
                <rect
                  x="88"
                  y="38"
                  width="14"
                  height="32"
                  fill="#7A5C2E"
                />
                <rect
                  x="104"
                  y="30"
                  width="18"
                  height="40"
                  fill="#A17A3C"
                />
                <rect
                  y="70"
                  width="140"
                  height="20"
                  fill="#4FA8D8"
                />
                <rect
                  y="70"
                  width="140"
                  height="3"
                  fill="#6CC1E8"
                />
              </svg>
            </div>
            <div class="min-w-0 flex-1">
              <h3 class="text-lg font-semibold leading-tight text-black-100 tablet:text-xl">Damac Lagoons</h3>
              <p class="mt-1 text-sm text-black/60">AED 2,200,000</p>
              <p class="text-sm text-black/60">{{ t('landing.payoutFeature.commission') }}</p>
            </div>
          </div>

          <i18n-t
            keypath="landing.payoutFeature.question"
            scope="global"
            tag="p"
            class="mb-4 border-b border-black/5 pb-4 text-base font-medium leading-snug text-black-100 tablet:text-lg"
          >
            <template #accent
              ><span class="italic text-[#3A3A3A]">{{ t('landing.payoutFeature.questionAccent') }}</span></template
            >
          </i18n-t>

          <div class="mt-5 grid grid-cols-2 gap-3 tablet:gap-4">
            <!-- Current brokerage -->
            <div class="relative rounded-xl bg-black/[0.03] p-4 tablet:p-5">
              <p class="text-[10px] font-semibold uppercase tracking-[0.15em] text-black/50">
                {{ t('landing.shared.currentBrokerage') }}
              </p>
              <p class="mt-3 whitespace-nowrap text-2xl font-black tabular-nums text-[#6B6B6B] tablet:text-3xl">
                AED {{ formatNumber(displayedCompetitor) }}
              </p>
              <div class="mt-2 h-1 w-full overflow-hidden rounded-full bg-black/5">
                <div
                  class="h-full rounded-full bg-black/20 transition-[width] duration-[1500ms] ease-out"
                  :style="{ width: barProgress * brokerageBarRatio * 100 + '%' }"
                ></div>
              </div>
              <p class="mt-3 text-sm text-black/50">
                {{ t('landing.payoutFeature.brokerageSplit', { percent: example.brokeragePercent }) }}
              </p>
              <div class="mt-4 border-t border-black/[0.06] pt-3">
                <p class="inline-flex items-center gap-1.5 text-sm text-black/50">
                  <svg
                    viewBox="0 0 24 24"
                    class="size-3.5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                    />
                    <path
                      d="M12 6v6l4 2"
                      stroke-linecap="round"
                    />
                  </svg>
                  {{ t('landing.payoutFeature.brokeragePaid') }}
                </p>
              </div>
            </div>

            <!-- Insiders -->
            <div class="relative rounded-xl bg-[#FAF6EC] p-4 tablet:p-5">
              <div class="pointer-events-none absolute inset-0 overflow-hidden rounded-xl">
                <div class="dic-shine absolute inset-0"></div>
              </div>
              <p class="relative text-[10px] font-semibold uppercase tracking-[0.15em] text-black/60">
                {{ t('landing.payoutFeature.atInsiders') }}
              </p>
              <p
                class="relative mt-3 whitespace-nowrap text-2xl font-black tabular-nums text-black-100 tablet:text-3xl"
              >
                AED {{ formatNumber(displayedPayout) }}
              </p>
              <div class="relative mt-2 h-1 w-full overflow-hidden rounded-full bg-black/5">
                <div
                  class="h-full rounded-full bg-gold-primary-120 transition-[width] duration-[1500ms] ease-out"
                  :style="{ width: barProgress * 100 + '%' }"
                ></div>
              </div>
              <p class="relative mt-3 text-sm font-medium text-gold-primary-120">
                {{ t('landing.payoutFeature.insidersSplit', { percent: example.insidersPercent }) }}
              </p>
              <div class="relative mt-4 border-t border-black/[0.08] pt-3">
                <p class="inline-flex items-center gap-1.5 text-sm text-black-100">
                  <svg
                    viewBox="0 0 24 24"
                    class="size-3.5 text-gold-primary-120"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                  {{ t('landing.payoutFeature.insidersPaid') }}
                </p>
              </div>
              <Transition name="delta">
                <div
                  v-if="showDelta"
                  class="pointer-events-none absolute -top-3 end-3 inline-flex items-center gap-1 rounded-full bg-gold-primary-120 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-black-100 shadow-md shadow-gold-primary-100/30"
                >
                  <svg
                    viewBox="0 0 24 24"
                    class="size-3"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                  >
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                    <polyline points="17 6 23 6 23 12" />
                  </svg>
                  {{ t('landing.payoutFeature.delta', { amount: formatNumber(example.deltaAed) }) }}
                </div>
              </Transition>
            </div>
          </div>

          <p class="mt-4 text-base font-semibold leading-snug text-black-100 tablet:text-lg">
            {{ t('landing.payoutFeature.footer') }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useIntersectionObserver, usePreferredReducedMotion } from '@vueuse/core';
import { payoutExample } from '~/utils/landing/facts';

const { t } = useI18n();
const { formatNumber } = useFormat();

// Keys under `landing.payoutFeature.tags`.
const tagPills = ['split', 'payouts', 'direct'] as const;

const { topSelfClosePercent } = useLandingFacts();
// Without a loaded tier config the section is hidden rather than showing a made-up split.
const example = computed(() => payoutExample(topSelfClosePercent.value ?? 0));
const descriptionParams = computed(() => ({
  percent: example.value.insidersPercent,
  brokerage: formatNumber(example.value.brokerageAed),
  insiders: formatNumber(example.value.insidersAed),
}));
const brokerageBarRatio = computed(() =>
  example.value.insidersAed > 0 ? Math.min(1, example.value.brokerageAed / example.value.insidersAed) : 0,
);

const reduced = usePreferredReducedMotion();
const cardRef = ref<HTMLElement | null>(null);
const isRevealed = ref(false);
const displayedCompetitor = ref(0);
const displayedPayout = ref(0);
const barProgress = ref(0);
const showDelta = ref(false);
const hasAnimated = ref(false);

const runAnimation = () => {
  if (hasAnimated.value) return;
  hasAnimated.value = true;
  if (reduced.value === 'reduce') {
    displayedCompetitor.value = example.value.brokerageAed;
    displayedPayout.value = example.value.insidersAed;
    barProgress.value = 1;
    showDelta.value = true;
    return;
  }
  const duration = 1500;
  const start = performance.now();
  const step = (now: number) => {
    const p = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - p, 3);
    displayedCompetitor.value = Math.floor(example.value.brokerageAed * eased);
    displayedPayout.value = Math.floor(example.value.insidersAed * eased);
    barProgress.value = eased;
    if (p < 1) requestAnimationFrame(step);
    else setTimeout(() => (showDelta.value = true), 300);
  };
  requestAnimationFrame(step);
};

useIntersectionObserver(
  cardRef,
  ([entry]) => {
    if (entry.isIntersecting) {
      isRevealed.value = true;
      setTimeout(runAnimation, 650);
    }
  },
  { threshold: 0.3 },
);
</script>

<style scoped lang="scss">
.dic-shine {
  background: linear-gradient(120deg, transparent 30%, rgba(255, 255, 255, 0.55) 50%, transparent 70%);
  transform: translateX(-100%);
  animation: shine 3.2s ease-in-out 0.9s 1 forwards;

  // RTL: sweep from the inline-start (right) edge instead.
  [dir='rtl'] & {
    transform: translateX(100%);
    animation-name: shine-rtl;
  }
}
@keyframes shine {
  to {
    transform: translateX(100%);
  }
}
@keyframes shine-rtl {
  to {
    transform: translateX(-100%);
  }
}
.delta-enter-active {
  transition:
    opacity 320ms ease,
    transform 320ms ease;
}
.delta-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
</style>
